import { Helmet } from "@dr.pogodin/react-helmet";
import { cn } from "@tine/designsystem-utils";
import { type ReactNode, useState } from "react";
import { reportError } from "../../../common/errorReporting";
import { logger } from "../../../common/logger";
import { sendSignup } from "../../../common/tracking";
import type { DynamicsCaptureConfig, DynamicsCaptureMapping } from "../../../common/types/dynamicsTypes";
import type { FormValuesToSubmit } from "../../../common/types/formTypes";
import { scriptUrl } from "../../../common/utils/dynamicsUtils";
import { sanitizePhoneNumber } from "../../../stores/notifications/recipientsUtils";
import Button from "../../Button";
import { validateEmail, validatePhone } from "../../formvalidation/formUtilities/fieldValidators";
import ValidatedForm from "../../formvalidation/ValidatedForm";
import ValidatedInput from "../../formvalidation/ValidatedInput";

const fieldValidators = {
   mandatory: undefined,
   email: validateEmail,
   phone: (value: string, required?: boolean) => validatePhone(sanitizePhoneNumber(value), required || value.trim() !== "")
};

type SignupFormProps = {
   values: { [fieldname: string]: string };
   mappings: DynamicsCaptureMapping[];
   config: DynamicsCaptureConfig;
   buttonText: string;
   trackingFormName?: string;
   children?: ReactNode;
   buttonVariant?: "primary" | "secondary";
   onSignup?: (payload: Record<string, string>) => void;
   canSubmit?: () => boolean;
   persistenceKey?: string;
};

const SignupForm = ({
   values,
   mappings,
   buttonText,
   config,
   buttonVariant = "primary",
   trackingFormName,
   children,
   onSignup,
   canSubmit,
   persistenceKey
}: SignupFormProps) => {
   const [signingUp, setSigningUp] = useState(false);

   const handleSignup = async (formValues: FormValuesToSubmit) => {
      if (signingUp || canSubmit?.() === false) {
         return { resetFormFields: false };
      }
      setSigningUp(true);

      const payload: Record<string, string> = {};

      const payloadFromMappings: Record<string, string> = {};

      for (const mapping of mappings) {
         const value = formValues[mapping.FormFieldName];
         if (value) {
            payloadFromMappings[mapping.FormFieldName] =
               mapping.validation === "phone" ? sanitizePhoneNumber(value as string) : (value as string);
         }
      }

      const formElement = document.createElement("form");
      for (const mapping of mappings) {
         const input = document.createElement("input");
         input.type = mapping.hidden ? "hidden" : "text";
         input.name = mapping.FormFieldName;
         input.value = payloadFromMappings[mapping.FormFieldName] || "";
         formElement.appendChild(input);
      }
      formElement.method = "POST";

      let error: string | Error | undefined;
      if (formElement) {
         try {
            const serializedForm = window.d365mktformcapture.serializeForm(formElement, mappings);
            const dynamicsPayload = serializedForm.SerializedForm.build();
            await window.d365mktformcapture.submitForm(config, dynamicsPayload);
         } catch (err) {
            error = typeof err === "string" || err instanceof Error ? err : "D365 form capture submission error";
            reportError(error, {
               context: "Signup form capture",
               signup: {
                  payload,
                  mappings
               }
            });
            logger.warn("contentful", err);
         }
      }

      if (error) {
         if (error instanceof Error) {
            payload.d365Error = error.message;
         } else if (typeof error === "string") {
            payload.d365Error = error;
         } else {
            payload.d365Error = "D365 form capture failed";
         }
      }

      if (onSignup) {
         onSignup(payload);
      }

      sendSignup(trackingFormName);
      setSigningUp(false);

      return { resetFormFields: true };
   };

   return (
      <>
         <Helmet>
            {!window.d365mktformcapture && (
               <script
                  src={scriptUrl}
                  onError={() => {
                     const error = new Error("Error loading script");
                     reportError(error, {
                        context: "Dynamics Form Capture script load",
                        signup: { scriptUrl: scriptUrl }
                     });
                     logger.warn("contentful", "Kunne ikke laste Dynamics Form Capture script");
                  }}
               />
            )}
         </Helmet>
         <ValidatedForm
            onSubmit={handleSignup}
            persistenceKey={persistenceKey}
            className="flex flex-col items-center w-full gap-2"
         >
            {mappings.map((mapping) => (
               <ValidatedInput
                  key={mapping.FormFieldName}
                  initialValue={values[mapping.FormFieldName] || ""}
                  label={mapping.label}
                  name={mapping.FormFieldName}
                  type={mapping.validation === "phone" ? "tel" : "text"}
                  className={cn("mb-2", { hidden: mapping.hidden })}
                  validator={mapping.validation ? fieldValidators[mapping.validation] : undefined}
                  required={!!mapping.validation}
               />
            ))}
            {children}
            <Button variant={buttonVariant} className="mt-3" loading={signingUp} type="submit">
               {buttonText}
            </Button>
         </ValidatedForm>
      </>
   );
};

export default SignupForm;
